import { apiClient } from '../../../api/client'
import { downloadXlsx, pruneEmpty, type ExportParams } from '../../../lib/download'
import type { MeetingLogEntry, PagedResponse } from '../../meetings/types'
import type {
  ActionDetail,
  ActionFollowState,
  ActionLevels,
  ActionListItem,
  ActionQuery,
  ActionScope,
  ActionStatusOption,
  ActionUpdateRequest,
  MinutePerson,
  PersonInput,
  ResponsibleRole,
  StandaloneActionCreateRequest,
  SubActionCreateRequest,
  SubActionUpdateRequest,
} from '../types'

const BASE = '/management/actions'

/** Toplantıdan bağımsız aksiyon oluştur (6.5.3). Oluşturulan aksiyonun detayı döner. */
export async function createStandaloneAction(
  body: StandaloneActionCreateRequest,
): Promise<ActionDetail> {
  const { data } = await apiClient.post<ActionDetail>(BASE, body)
  return data
}

/**
 * İşlem Geçmişi (074): bu aksiyonun <b>ve aktif alt aksiyonlarının</b> denetim kayıtları — zaman tüneli
 * (en yeni önce). LOG_INFO'daki statü/atama id'leri backend'de ada çözülür. Denetim DTO'su toplantıyla ortak
 * ({@code MeetingLogEntry}).
 */
/**
 * Aksiyon görüntüleme izi (denetim kodu 536).
 *
 * <p>Görüntüleme bir yazma işlemi olmadığı için izi düşürecek doğal bir akış yok; arayüz açıkça
 * tetikler (kod 500 ile aynı desen).
 *
 * <p>Hata **yutulur**: denetim izi kullanıcının ekranı açmasını engellememelidir. Sunucu tarafı da
 * best-effort yazar.
 */
export async function recordActionViewAccess(id: number): Promise<void> {
  try {
    await apiClient.post(`/management/actions/${id}/view-access`)
  } catch {
    // Denetim izi kullanıcı akışını bozmaz.
  }
}

export async function getActionLogs(id: number): Promise<MeetingLogEntry[]> {
  const { data } = await apiClient.get<MeetingLogEntry[]>(`${BASE}/${id}/logs`)
  return data
}

/** Durum filtresi/etiketi için statüler — REFERANS(MEETING_ACTION_STATUS)'tan dinamik. */
export async function getActionStatuses(): Promise<ActionStatusOption[]> {
  const { data } = await apiClient.get<ActionStatusOption[]>('/management/actions/statuses')
  return data
}

/** Aksiyon editörü için picklist seçenekleri: Aciliyet Derecesi + Önem Derecesi (REFERANS). */
export async function getActionLevels(): Promise<ActionLevels> {
  const { data } = await apiClient.get<ActionLevels>('/management/actions/levels')
  return data
}

/**
 * Yalnız `true` iken gönderilen bayrak süzgeci; kapalıyken parametre HİÇ gönderilmez → parametre
 * yokken sunucu davranışı birebir korunur.
 */
function flag(on: boolean | undefined): 'true' | undefined {
  return on ? 'true' : undefined
}

/**
 * Çoklu seçim → virgüllü liste (Spring `List<Long>` bağlaması — axios dizi köşeli-ayraç ekini
 * bağlamaz). Boş seçim → parametre yok.
 */
function csvIds(ids: number[] | undefined): string | undefined {
  return ids?.length ? ids.join(',') : undefined
}

/**
 * **Kapsam-özel** süzgeçler: yalnız `scope` gönderilen uçlarda (aktif liste ve onun Excel'i) anlamlıdır
 * — arşiv tek kümedir, orada bunların hiçbiri gönderilmez.
 */
function scopedFilterParams(query: ActionQuery): ExportParams {
  return {
    scope: query.scope === 'all' ? undefined : query.scope,
    // 053 ve 2026-08-21 — Anasayfa panosunun bağlantılarının bayrak süzgeçleri (bkz. flag).
    criticalOrOverdue: flag(query.criticalOrOverdue),
    overdue: flag(query.overdueOnly),
    upcoming: flag(query.upcomingOnly),
    delegated: flag(query.delegatedOnly),
    // Dönem penceresi: anahtar gönderilir, aralığa çevrimi sunucu yapar (panoyla tek kaynak).
    period: query.period,
    from: query.periodFrom,
    to: query.periodTo,
    // 071 — Tür filtresi (yalnız Oluşturduklarım/Bana Atananlar): false=ana(kök) / true=alt; null → hepsi.
    subAction: query.subAction == null ? undefined : String(query.subAction),
    // Kişi süzgeçleri de kapsam-özeldir (aktif alt sayfalar; arşivde yok). 2026-09-15'te AYRILDI:
    // önceden tek parametre Ana VEYA Yan rolünde arıyordu, artık her rolün kendi parametresi var.
    mainResponsibleEmployeeId: query.mainResponsibleEmployeeId,
    supportManagerEmployeeId: query.supportManagerEmployeeId,
  }
}

/**
 * Aksiyon **süzgeç parametreleri** — liste, arşiv ve Excel uçları tek yerden beslenir: "ekranda
 * gördüğüm küme" ile "indirdiğim küme" ayrışamaz ve yeni bir süzgeç eklenirken tek nokta güncellenir.
 * (Dışa aktarma, liste çağrısının sayfalama olmadan hâlidir: sunucu tüm filtreli kayıtları döndürür.)
 *
 * <p>Boş süzgeçler (`''`/`null`/`undefined`) {@link pruneEmpty} ile ayıklanır → sunucu "boş filtre"
 * görmez; metin süzgeçleri de orada kırpılır.
 *
 * @param withScope kapsam ve kapsam-özel süzgeçler ({@link scopedFilterParams}) gönderilsin mi
 */
function filterParams(query: ActionQuery, withScope: boolean): ExportParams {
  return {
    q: query.q,
    importanceLevelId: query.importanceLevelId,
    statusIds: csvIds(query.statusIds),
    // "Geciken" filtresi arşivde de anlamlıdır → kapsam-özel DEĞİL.
    delayedUpdated: flag(query.delayedUpdated),
    sort: query.sort,
    ...(withScope ? scopedFilterParams(query) : {}),
  }
}

/** Liste uçlarının parametreleri: süzgeçler + sayfalama (`page`/`size` her zaman gönderilir). */
function listParams(
  query: ActionQuery,
  withScope: boolean,
): Record<string, string | number | boolean> {
  return pruneEmpty({
    page: query.page ?? 0,
    size: query.size ?? 20,
    ...filterParams(query, withScope),
  })
}

/** Tüm Aksiyonlar listesi (sayfalı/aranabilir/filtrelenebilir/sıralanabilir). */
export async function getActions(query: ActionQuery): Promise<PagedResponse<ActionListItem>> {
  const { data } = await apiClient.get<PagedResponse<ActionListItem>>('/management/actions', {
    params: listParams(query, true),
  })
  return data
}

/**
 * Kişi süzgeçlerinin seçenekleri — ilgili **kapsamda** ve **istenen rolde** atanmış kişiler (sicil
 * bazında tekil, ada göre sıralı). Ekranın kendi süzgeçleri (arama/statü/önem) uygulanmaz: seçenekler
 * filtre değiştikçe kaybolmaz.
 *
 * `role` verilmezse sunucu eski **birleşik** kümeyi (Ana + Yan) döndürür; ekran her zaman rol gönderir.
 */
export async function getResponsibleOptions(
  scope?: ActionScope,
  role?: ResponsibleRole,
): Promise<MinutePerson[]> {
  const scoped = scope && scope !== 'all' ? { scope } : undefined
  const params = role ? { ...scoped, role } : scoped
  const { data } = await apiClient.get<MinutePerson[]>('/management/actions/responsibles', {
    params,
  })
  return data
}

/** Arşivlenen Aksiyonlar (analiz 8.4/8.5): yalnız Arşivlendi/İptal statüsündekiler (salt-okuma). Kapsam gönderilmez. */
export async function getArchivedActions(
  query: ActionQuery,
): Promise<PagedResponse<ActionListItem>> {
  const { data } = await apiClient.get<PagedResponse<ActionListItem>>(
    '/management/actions/archived',
    { params: listParams(query, false) },
  )
  return data
}

// ── Aksiyon Detay (analiz 7) ──

/** Aksiyon detayı (bilgiler + termin + takip edenler + alt aksiyonlar + dosyalar). */
export async function getActionDetail(id: number): Promise<ActionDetail> {
  const { data } = await apiClient.get<ActionDetail>(`${BASE}/${id}`)
  return data
}

/**
 * Tanım/açıklama/termin + opsiyonel not güncelle (JSON). Not yalnız metindir (dosya eki yok); termin
 * değişince not zorunludur. Güncel detay döner (bayat modDate → 409).
 */
export async function updateAction(id: number, body: ActionUpdateRequest): Promise<ActionDetail> {
  const { data } = await apiClient.put<ActionDetail>(`${BASE}/${id}`, body)
  return data
}

/**
 * Görevi sil (067/6.5.5; 092; 101) — <b>soft-delete</b> (RECORD_STATUS=2) + kademeli alt görev. 204.
 * Yetki sunucuda tek koddur: `RECORD_DELETE` (2026-09-11 — sahiplik/`CRE_BY` ölçütü ve onunla birlikte
 * `ACTION_DELETE_OWN`/`ACTION_DELETE_ANY` modeli terk edildi); yetkisiz → 403, kapsam dışı → 404.
 *
 * <p><b>Bu uç yalnız KÖK görev içindir.</b> Alt görev {@link deleteSubAction} ile silinir — bu uç onun
 * yan etkilerini (üstteki Süreç Destek Yöneticisi atamasının kaldırılması, SUBACTION_DELETED izi)
 * yapmaz.
 */
export async function deleteAction(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`)
}

/** Aksiyon notu düzenle (yalnız metin; ekleyen/zaman korunur). Güncel aksiyon detayı döner. `ACTION_NOTE_MANAGE`. */
export async function updateActionNote(
  id: number,
  noteId: number,
  note: string,
  modDate: string,
): Promise<ActionDetail> {
  const { data } = await apiClient.put<ActionDetail>(`${BASE}/${id}/notes/${noteId}`, {
    note,
    modDate,
  })
  return data
}

/** Aksiyon notu sil — soft-delete. Güncel aksiyon detayı döner. `ACTION_NOTE_MANAGE`. */
export async function deleteActionNote(id: number, noteId: number): Promise<ActionDetail> {
  const { data } = await apiClient.delete<ActionDetail>(`${BASE}/${id}/notes/${noteId}`)
  return data
}

/**
 * Statü değiştir (izin verilen geçiş + optimistic lock). 204. 066/AK-12: aksiyon Gecikti'den (termini geçmiş)
 * AÇIK bir statüye çıkarken {@code newDeadline} zorunlu (her iki rol) ve ana/kök aksiyonda {@code note} zorunlu
 * (alt aksiyonda not yok — AK-7); backend bunları doğrular (400 `deadlineRequired`/`noteRequired`).
 */
export async function changeActionStatus(
  id: number,
  toStatusId: number,
  modDate: string,
  newDeadline?: string | null,
  note?: string | null,
  completionNote?: string | null,
): Promise<void> {
  await apiClient.put(`${BASE}/${id}/status`, {
    toStatusId,
    modDate,
    newDeadline: newDeadline ?? null,
    note: note ?? null,
    // 105 — YALNIZ hedef 331 iken yazılır; sunucu diğer hedeflerde yok sayar.
    //
    // 29.09.2026'dan beri o hedefte ZORUNLU: boş dize de, alanın hiç gönderilmemesi de 400
    // (`completionNoteRequired`) döner. Eski "üç değil iki durum" ayrımı (undefined = koru,
    // '' = sil) sözleşmede hâlâ geçerli ama artık ERİŞİLEMEZ — `CompletionNoteDialog` yalnız dolu
    // metin üretir ve statü değiştirmenin başka istemci yolu yok.
    completionNote: completionNote,
  })
}

/** Bir aksiyonun mevcut statüsünden izin verilen hedef statü id'leri. */
export async function getAllowedTransitions(id: number): Promise<number[]> {
  const { data } = await apiClient.get<number[]>(`${BASE}/${id}/allowed-transitions`)
  return data
}

/**
 * Görevi **kendi adına** takibe al ya da takibi bırak (120 — yıldız). Gövde yoktur: takip eden daima
 * giriş yapan kullanıcıdır. İki uç da kendini tekrar etmeye dayanıklıdır; güncel durum döner.
 * Başkasını takipçi ekleyen/çıkaran eski uçlar (`/followers`) kaldırıldı.
 */
export async function setActionFollowed(id: number, followed: boolean): Promise<ActionFollowState> {
  const url = `${BASE}/${id}/follow`
  const { data } = followed
    ? await apiClient.put<ActionFollowState>(url)
    : await apiClient.delete<ActionFollowState>(url)
  return data
}

/**
 * Yan Sorumlu ekle (sicil bazında tekil). Güncel sorumlu listesi döner.
 *
 * <p>Geciken aksiyonda uç **400 `overdueAssignee`** döner (kullanıcı kuralı 2026-08-17: önce hedef
 * teslim tarihi güncellenir). Gövdenin eski `newDeadline`/`note` alanları bu yüzden **kaldırıldı** —
 * 066/FR-013'ün (AK-12) "yeni termin vererek ekle" dalı artık yok.
 */
export async function addActionAssignee(id: number, person: PersonInput): Promise<MinutePerson[]> {
  const { data } = await apiClient.post<MinutePerson[]>(`${BASE}/${id}/assignees`, {
    assignee: person,
  })
  return data
}

/** Sorumlu çıkar (sicil). Güncel sorumlu listesi döner. */
export async function removeActionAssignee(
  id: number,
  employeeId: string,
): Promise<MinutePerson[]> {
  const { data } = await apiClient.delete<MinutePerson[]>(`${BASE}/${id}/assignees/${employeeId}`)
  return data
}

/**
 * **Ana Sorumlu** ata/değiştir (2026-08-12) — tekil ve zorunlu olduğundan "temizle" ucu yoktur; bu uç
 * eskisinin yerine yenisini koyar. Alt aksiyonda kullanılmaz (400 `notRootAction`). Güncel detay döner.
 */
export async function setMainResponsible(id: number, person: PersonInput): Promise<ActionDetail> {
  const { data } = await apiClient.put<ActionDetail>(`${BASE}/${id}/main-responsible`, person)
  return data
}

/**
 * Alt aksiyon oluştur — sorumlu başına bir alt görev (20.10.2026). Güncel ÜST detay döner.
 *
 * <p><b>Tek sorumluda gövde eski biçimde gider</b> ({@code assignee: {...}}), yalnız 2+ sorumluda
 * {@code assignees: [...]}: THY frontend ile backend'i ayrı depolardan ve ayrı zamanlarda yayına
 * alıyor; frontend önce çıkarsa eski backend {@code assignees}'ı tanımaz, zorunlu {@code assignee}'yi
 * bulamaz ve <b>her</b> alt görev oluşturmayı 400'le reddederdi (frontend'in önce merge edildiği bir
 * tur 11.09.2026'da yaşandı — `ci/thy/README.md`). Yeni backend iki biçimi de kabul eder
 * ({@code SubActionCreateRequest#assignees} → {@code @JsonAlias("assignee")}). İki anahtar <b>birlikte</b>
 * gönderilmez: aynı alana iki ad verildiğinde Jackson'ın hangisini seçtiği ölçülmedi.
 */
export async function createSubAction(
  id: number,
  body: SubActionCreateRequest,
): Promise<ActionDetail> {
  const { assignees, ...rest } = body
  const wire = assignees.length === 1 ? { ...rest, assignee: assignees[0] } : body
  const { data } = await apiClient.post<ActionDetail>(`${BASE}/${id}/subactions`, wire)
  return data
}

/**
 * Alt aksiyon alan güncelle (ayrı Alt Aksiyon Detay sayfası): Tanım + Termin + Atanan. Alt aksiyona not/doküman
 * yoktur (AK-7). Güncel alt aksiyon detayı döner (kök aksiyonda 400).
 */
export async function updateSubAction(
  id: number,
  body: SubActionUpdateRequest,
): Promise<ActionDetail> {
  const { data } = await apiClient.put<ActionDetail>(`${BASE}/${id}/subaction`, body)
  return data
}

/** Alt aksiyon Sorumlusu (Atanan) değiştir (073) — SAP/HR kişisi. Güncel alt aksiyon detayı döner. */
export async function setSubActionAssignee(id: number, body: PersonInput): Promise<ActionDetail> {
  const { data } = await apiClient.put<ActionDetail>(`${BASE}/${id}/subaction/assignee`, body)
  return data
}

/**
 * Alt görevi sil (066/7.16; 101) — soft-delete + ana durum türetimi. Güncel ÜST görev detayı döner.
 *
 * <p><b>2026-09-11'den beri OTOMATİK alt görev de silinir</b> (javadoc o güne kadar "yalnız manuel,
 * otomatik → 400" diyordu ve bayattı): ayrım serviste yapılır — otomatik kayıt yalnız `RECORD_DELETE`
 * ile silinir (yalnız `ACTION_MANAGE` taşıyan kullanıcı <b>403</b> alır, 400 değil) ve silinirken onu
 * doğuran Süreç Destek Yöneticisi ataması üstten de kaldırılır.
 *
 * <p>O kaldırma üst görevi <b>sorumlusuz bırakacaksa</b> işlem reddedilir: 400 `atLeastOneAssignee`
 * (ekranda nedenini söyleyen metne eşlenir — bkz. `subActionDeleteErrorKey`). Kök/bağımsız görevle
 * çağrılırsa 400.
 */
export async function deleteSubAction(id: number): Promise<ActionDetail> {
  const { data } = await apiClient.delete<ActionDetail>(`${BASE}/${id}/subaction`)
  return data
}

/** Aksiyona dosya(lar) ekle (multipart, pending-first). */
export async function uploadActionAttachments(id: number, files: File[]): Promise<void> {
  const form = new FormData()
  files.forEach((f) => form.append('file', f))
  await apiClient.post(`${BASE}/${id}/attachments`, form)
}

/** Aksiyondan dosya ekini kaldır (soft-delete). */
export async function deleteActionAttachment(id: number, attachmentId: number): Promise<void> {
  await apiClient.delete(`${BASE}/${id}/attachments/${attachmentId}`)
}

/**
 * Aksiyon listesini Excel (.xlsx) olarak indirir (liste ekranıyla aynı filtre/sıralama).
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportActions(query: ActionQuery): Promise<void> {
  await downloadXlsx(`${BASE}/export`, filterParams(query, true), 'gorevler.xlsx')
}

/**
 * Arşivlenen aksiyonları Excel (.xlsx) olarak indirir. Kapsam gönderilmez (arşiv tek küme).
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportArchivedActions(query: ActionQuery): Promise<void> {
  await downloadXlsx(`${BASE}/archived/export`, filterParams(query, false), 'gorev-arsivi.xlsx')
}
